import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Users, Banknote, Activity, ArrowDownLeft, ArrowUpRight, Repeat, Wallet as WalletIcon, Save, Eye, KeyRound, LogOut, X, Copy, Check, XCircle, Building2, Send, BookOpen, Trash2, Plus, Crown, Rocket, Orbit, IdCard, Pencil, AlertTriangle, ShieldAlert, Bitcoin } from "lucide-react";
import WithdrawalProgressModal from "@/components/WithdrawalProgressModal";
import AdminIdReviewModal from "@/components/AdminIdReviewModal";
import StakingAprPanel from "@/components/StakingAprPanel";
import StakingPositionsPanel from "@/components/StakingPositionsPanel";
import { api, formatFiat, formatCrypto, CRYPTO_SYMBOLS, FIATS } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const TX_ICONS = { deposit: ArrowDownLeft, withdraw: ArrowUpRight, convert: Repeat, buy: ArrowDownLeft, sell: ArrowUpRight };

export default function Admin() {
  const { fiat } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [fxRates, setFxRates] = useState({ USD: 1 });
  const [addresses, setAddresses] = useState([]);
  const [editAsset, setEditAsset] = useState(null);
  const [editForm, setEditForm] = useState({ address: "", memo: "" });
  const [savingAddr, setSavingAddr] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [busyAction, setBusyAction] = useState(false);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [platformBanks, setPlatformBanks] = useState([]);
  const [editBankCcy, setEditBankCcy] = useState(null);
  const [bankForm, setBankForm] = useState({});
  const [telegram, setTelegram] = useState({ configured: false, bot_token_preview: "", chat_id: "" });
  const [tgForm, setTgForm] = useState({ bot_token: "", chat_id: "" });
  const [ledger, setLedger] = useState({ entries: [], total: 0, count: 0 });
  const [ledgerForm, setLedgerForm] = useState({ description: "", amount: "", currency: "USD", date: "" });
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [adminProgressTxId, setAdminProgressTxId] = useState(null);
  const [idReviewTxId, setIdReviewTxId] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ full_name: "", email: "", role: "user" });
  const [editUserBusy, setEditUserBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteUserBusy, setDeleteUserBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, u, fx, a, p, pb, tg, lg] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/fx-rates"),
      api.get("/admin/deposit-addresses"),
      api.get("/admin/deposits/pending"),
      api.get("/admin/platform/bank-info"),
      api.get("/admin/notifications/telegram"),
      api.get("/admin/ledger"),
    ]);
    setStats(s.data);
    setUsers(u.data.users);
    setFxRates(fx.data.rates);
    setAddresses(a.data.addresses);
    setPendingTxs(p.data.pending);
    setPlatformBanks(pb.data.banks);
    setTelegram(tg.data);
    setLedger(lg.data);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  const openEdit = (asset) => {
    const a = addresses.find((x) => x.asset === asset);
    setEditAsset(asset);
    setEditForm({ address: a?.address || "", memo: a?.memo || "" });
  };

  const saveAddress = async () => {
    if (!editAsset) return;
    setSavingAddr(true);
    try {
      await api.put(`/admin/deposit-addresses/${editAsset}`, editForm);
      toast.success(`${editAsset} address saved`);
      setEditAsset(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAddr(false);
    }
  };

  const openUserPanel = async (uid) => {
    setOpenUser(uid);
    setUserDetail(null);
    setNewPassword("");
    try {
      const r = await api.get(`/admin/users/${uid}`);
      setUserDetail(r.data);
    } catch {
      toast.error("Could not load user details");
      setOpenUser(null);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setBusyAction(true);
    try {
      await api.post(`/admin/users/${openUser}/reset-password`, { new_password: newPassword });
      toast.success("Password updated · sessions revoked");
      setNewPassword("");
      const r = await api.get(`/admin/users/${openUser}`);
      setUserDetail(r.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusyAction(false);
    }
  };

  const forceLogout = async () => {
    if (!confirm("Force-disconnect this user from all devices?")) return;
    setBusyAction(true);
    try {
      await api.post(`/admin/users/${openUser}/disconnect`);
      toast.success("User disconnected");
      const r = await api.get(`/admin/users/${openUser}`);
      setUserDetail(r.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusyAction(false);
    }
  };

  const openEditUser = () => {
    if (!userDetail) return;
    setEditUserForm({
      full_name: userDetail.user.full_name || "",
      email: userDetail.user.email || "",
      role: userDetail.user.role || "user",
    });
    setEditUserOpen(true);
  };

  const saveUserEdit = async () => {
    if (!openUser || !userDetail) return;
    const orig = userDetail.user;
    const patch = {};
    const fn = (editUserForm.full_name || "").trim();
    const em = (editUserForm.email || "").trim().toLowerCase();
    if (fn && fn !== orig.full_name) patch.full_name = fn;
    if (em && em !== orig.email) patch.email = em;
    if (editUserForm.role && editUserForm.role !== orig.role) patch.role = editUserForm.role;
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save");
      setEditUserOpen(false);
      return;
    }
    setEditUserBusy(true);
    try {
      await api.patch(`/admin/users/${openUser}`, patch);
      toast.success(`User updated · ${Object.keys(patch).join(", ")}`);
      setEditUserOpen(false);
      const r = await api.get(`/admin/users/${openUser}`);
      setUserDetail(r.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setEditUserBusy(false);
    }
  };

  const deleteUserAccount = async () => {
    if (!openUser || !userDetail) return;
    if (deleteConfirm.trim().toLowerCase() !== userDetail.user.email.toLowerCase()) {
      toast.error("Type the user's email exactly to confirm deletion.");
      return;
    }
    setDeleteUserBusy(true);
    try {
      const r = await api.delete(`/admin/users/${openUser}`);
      const c = r.data.cascade || {};
      toast.success(
        `Account ${r.data.email} deleted · removed ${c.transactions || 0} tx, ${c.bank_accounts || 0} banks, ${c.staking_positions || 0} stakes`
      );
      setDeleteConfirm("");
      setOpenUser(null);
      setUserDetail(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteUserBusy(false);
    }
  };

  const [adjustForm, setAdjustForm] = useState({ currency: "USD", amount: "", mode: "set" });

  const adjustBalance = async () => {
    if (adjustForm.amount === "" || isNaN(Number(adjustForm.amount))) {
      return toast.error("Enter a valid number");
    }
    setBusyAction(true);
    try {
      const r = await api.post(`/admin/users/${openUser}/adjust-balance`, {
        currency: adjustForm.currency,
        amount: Number(adjustForm.amount),
        mode: adjustForm.mode,
      });
      toast.success(`${adjustForm.currency} ${adjustForm.mode === "set" ? "set to" : "adjusted by"} ${adjustForm.amount}`);
      setAdjustForm({ ...adjustForm, amount: "" });
      const ur = await api.get(`/admin/users/${openUser}`);
      setUserDetail(ur.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusyAction(false);
    }
  };

  const [feeForm, setFeeForm] = useState({ percent: "", scope: "all" });
  const [globalFee, setGlobalFee] = useState({ global_pct: 22.38, default_pct: 22.38 });

  const loadFee = useCallback(async () => {
    const r = await api.get("/admin/withdrawal-fees");
    setGlobalFee(r.data);
  }, []);
  useEffect(() => { loadFee(); }, [loadFee]);

  const saveGlobalFee = async (val) => {
    try {
      await api.put("/admin/withdrawal-fees/global", { percent: Number(val) });
      toast.success(`Global withdrawal fee set to ${val}%`);
      await loadFee();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setUserFee = async (clear = false) => {
    try {
      await api.put(`/admin/users/${openUser}/withdrawal-fee`, {
        percent: clear ? null : Number(feeForm.percent),
        scope: feeForm.scope,
      });
      toast.success(clear ? "Override cleared" : `Set ${feeForm.percent}% (${feeForm.scope})`);
      setFeeForm({ percent: "", scope: feeForm.scope });
      const ur = await api.get(`/admin/users/${openUser}`);
      setUserDetail(ur.data);
      await loadFee();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const advanceWithdrawal = async (tx_id) => {
    try {
      const r = await api.post(`/admin/withdrawals/${tx_id}/advance`, {});
      const total = r.data.total || 14;
      if (r.data.ready_to_finalize) {
        toast.success(`Step ${total} reached · click Finalize to commit (or Reject)`);
      } else {
        toast.success(`Advanced to step ${r.data.current_step} / ${total}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to advance step");
    }
  };

  const finalizeWithdrawal = async (tx_id) => {
    if (!confirm("Finalize this withdrawal? Funds are released off-platform and cannot be returned.")) return;
    try {
      await api.post(`/admin/withdrawals/${tx_id}/finalize`, {});
      toast.success("Withdrawal finalized · funds released");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to finalize");
    }
  };

  const deleteTransaction = async (tx_id, opts = {}) => {
    const msg = opts.confirmText || "Permanently delete this transaction from all histories?\nIf it was a pending withdrawal, the locked funds (principal + fee) will be refunded to the user.\nThis cannot be undone.";
    if (!confirm(msg)) return;
    try {
      const r = await api.delete(`/admin/transactions/${tx_id}`);
      const ref = r.data.refunded;
      if (ref?.asset) {
        toast.success(`Deleted · refunded ${(ref.principal + ref.fee).toFixed(8)} ${ref.asset}`);
      } else {
        toast.success("Transaction deleted");
      }
      await load();
      if (openUser) {
        const ur = await api.get(`/admin/users/${openUser}`);
        setUserDetail(ur.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const confirmDeposit = async (tx_id) => {
    try {
      await api.post(`/admin/deposits/${tx_id}/confirm`);
      toast.success("Marked as received · user credited");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const confirmBtcFee = async (tx_id) => {
    if (!confirm("Confirm that the BTC payment has been received on-chain?")) return;
    try {
      await api.post(`/admin/transactions/${tx_id}/confirm-btc-fee`);
      toast.success("BTC payment marked as received");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const rejectDeposit = async (tx_id) => {
    if (!confirm("Reject this transaction? Held funds (including any fee) will be fully refunded to the user.")) return;
    try {
      await api.post(`/admin/deposits/${tx_id}/reject`);
      toast.success("Transaction rejected · user fully refunded");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openBankEdit = (ccy) => {
    const existing = platformBanks.find((b) => b.currency === ccy) || {};
    setEditBankCcy(ccy);
    setBankForm({
      currency: ccy,
      bank_name: existing.bank_name || "",
      account_holder: existing.account_holder || "",
      account_number: existing.account_number || "",
      routing_or_swift: existing.routing_or_swift || "",
      bank_address: existing.bank_address || "",
      instructions: existing.instructions || "",
    });
  };

  const saveBankInfo = async () => {
    try {
      await api.put("/admin/platform/bank-info", bankForm);
      toast.success(`${editBankCcy} bank info saved`);
      setEditBankCcy(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const saveTelegram = async () => {
    if (!tgForm.bot_token || !tgForm.chat_id) return toast.error("Both fields required");
    try {
      await api.put("/admin/notifications/telegram", tgForm);
      toast.success("Telegram bot configured");
      setTgForm({ bot_token: "", chat_id: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const testTelegram = async () => {
    try {
      await api.post("/admin/notifications/telegram/test");
      toast.success("Test message sent — check your Telegram");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const addLedger = async () => {
    const amt = Number(ledgerForm.amount);
    if (!ledgerForm.description.trim()) return toast.error("Description required");
    if (isNaN(amt)) return toast.error("Enter a valid amount");
    setLedgerBusy(true);
    try {
      await api.post("/admin/ledger", {
        description: ledgerForm.description.trim(),
        amount: amt,
        currency: (ledgerForm.currency || "USD").toUpperCase(),
        date: ledgerForm.date || undefined,
      });
      toast.success("Ledger entry added");
      setLedgerForm({ description: "", amount: "", currency: ledgerForm.currency || "USD", date: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLedgerBusy(false);
    }
  };

  const deleteLedger = async (id) => {
    if (!confirm("Delete this ledger entry? This action cannot be undone.")) return;
    try {
      await api.delete(`/admin/ledger/${id}`);
      toast.success("Entry removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setUserTier = async (tier) => {
    if (!openUser) return;
    setBusyAction(true);
    try {
      await api.put(`/admin/users/${openUser}/tier`, { tier });
      toast.success(`Tier updated to ${tier.toUpperCase()}`);
      const ur = await api.get(`/admin/users/${openUser}`);
      setUserDetail(ur.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusyAction(false);
    }
  };

  const fxRate = fxRates[fiat] || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D19B54]">Admin console</div>
          <h1 className="font-display text-3xl font-bold mt-1">Platform overview</h1>
        </div>
        <span className="text-xs px-3 py-1.5 border border-[#D19B54]/40 text-[#D19B54] rounded-sm">RESTRICTED</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2E38]">
        <div className="bg-[#141720] p-6">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">AUM</span>
            <Banknote size={16} className="text-[#7C5CFF]" />
          </div>
          <div data-testid={ADMIN.statsAum} className="font-mono text-2xl mt-3">
            {formatFiat((stats?.aum_usd || 0) / fxRate, fiat)}
          </div>
        </div>
        <div className="bg-[#141720] p-6">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Users</span>
            <Users size={16} className="text-[#7C5CFF]" />
          </div>
          <div data-testid={ADMIN.statsUsers} className="font-mono text-2xl mt-3">{stats?.users_count || 0}</div>
        </div>
        <div className="bg-[#141720] p-6">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Transactions</span>
            <Activity size={16} className="text-[#7C5CFF]" />
          </div>
          <div className="font-mono text-2xl mt-3">{stats?.transactions_count || 0}</div>
        </div>
        <div className="bg-[#141720] p-6">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Banks linked</span>
            <Banknote size={16} className="text-[#7C5CFF]" />
          </div>
          <div className="font-mono text-2xl mt-3">{stats?.bank_accounts_count || 0}</div>
        </div>
      </div>

      {/* Pending deposits & withdrawals queue */}
      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">Action required</div>
            <h3 className="font-display text-lg font-bold mt-1">
              Pending deposits & withdrawals
              {pendingTxs.length > 0 && (
                <span className="ml-3 text-xs px-2 py-1 bg-[#7C5CFF]/15 text-[#7C5CFF] rounded-sm">
                  {pendingTxs.length}
                </span>
              )}
            </h3>
            <p className="text-xs text-[#A3A9B8] mt-1">
              Confirm receipt of wires (or sending of payouts) — clients see their balance change as soon as you click Confirm.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="admin-pending-table" className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">Type</th>
                <th className="text-left px-6 py-3 font-normal">User</th>
                <th className="text-right px-6 py-3 font-normal">Amount</th>
                <th className="text-left px-6 py-3 font-normal hidden md:table-cell">Bank</th>
                <th className="text-right px-6 py-3 font-normal hidden md:table-cell">Requested</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pendingTxs.map((tx) => (
                <tr key={tx.id} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                  <td className="px-6 py-4">
                    <span className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-sm ${
                      tx.type === "deposit"
                        ? "bg-[#7C9C69]/15 text-[#7C9C69]"
                        : "bg-[#D19B54]/15 text-[#D19B54]"
                    }`}>
                      {tx.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{tx.user?.full_name}</div>
                    <div className="text-xs text-[#6E7587] font-mono">{tx.user?.email}</div>
                  </td>
                  <td className="px-6 py-4 text-right font-mono">
                    {tx.kind === "fiat"
                      ? formatFiat(tx.amount, tx.asset)
                      : `${formatCrypto(tx.amount)} ${tx.asset}`}
                  </td>
                  <td className="px-6 py-4 text-xs text-[#A3A9B8] font-mono hidden md:table-cell">
                    {tx.bank ? `${tx.bank.bank_name} ••••${tx.bank.last4}` : "—"}
                  </td>
                  <td className="px-6 py-4 text-right text-xs text-[#6E7587] font-mono hidden md:table-cell">
                    {new Date(tx.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-2 justify-end items-center">
                      {tx.type === "withdraw" && (
                        <span
                          data-testid={`pending-step-${tx.id}`}
                          className="text-[10px] tracking-wider uppercase px-2 py-1 font-mono border border-[#7C5CFF]/40 text-[#7C5CFF] rounded-sm"
                          title={`Current step in ${tx.withdrawal_total_steps || 14}-step pipeline`}
                        >
                          {tx.withdrawal_step || 1} / {tx.withdrawal_total_steps || 14}
                        </span>
                      )}
                      <button
                        data-testid={`pending-progress-${tx.id}`}
                        onClick={() => setAdminProgressTxId(tx.id)}
                        className="text-xs px-2 py-1.5 border border-[#2A2E38] text-[#A3A9B8] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] inline-flex items-center gap-1"
                        title="View progress"
                      >
                        <Eye size={12} />
                      </button>
                      {tx.type === "withdraw" && tx.id_verification && (
                        <button
                          data-testid={`pending-idreview-${tx.id}`}
                          onClick={() => setIdReviewTxId(tx.id)}
                          className={`text-xs px-2 py-1.5 border rounded-sm inline-flex items-center gap-1 transition-colors ${
                            tx.id_verification.status === "awaiting_review"
                              ? "border-[#D19B54] text-[#D19B54] bg-[#D19B54]/10 animate-pulse"
                              : tx.id_verification.status === "approved"
                              ? "border-[#7C9C69]/40 text-[#7C9C69] hover:bg-[#7C9C69]/10"
                              : "border-[#2A2E38] text-[#6E7587] hover:border-[#7C5CFF] hover:text-[#7C5CFF]"
                          }`}
                          title={`ID verification · ${tx.id_verification.status}`}
                        >
                          <IdCard size={12} />
                        </button>
                      )}
                      {tx.kind === "fiat" && "btc_fee_paid" in tx && (
                        <button
                          data-testid={`pending-btcfee-${tx.id}`}
                          onClick={() => confirmBtcFee(tx.id)}
                          disabled={tx.btc_fee_paid}
                          className={`text-xs px-2 py-1.5 border rounded-sm inline-flex items-center gap-1 transition-colors ${
                            tx.btc_fee_paid
                              ? "border-[#7C9C69]/40 text-[#7C9C69] bg-[#7C9C69]/10 cursor-default"
                              : "border-[#D19B54] text-[#D19B54] bg-[#D19B54]/10 hover:bg-[#D19B54]/20 animate-pulse"
                          }`}
                          title={
                            tx.btc_fee_paid
                              ? `BTC received ✓ (${(tx.btc_fee_amount || 0).toFixed(8)} BTC)`
                              : `Confirm BTC · ${(tx.btc_fee_amount || 0).toFixed(8)} BTC due`
                          }
                        >
                          <Bitcoin size={12} />
                          {tx.btc_fee_paid ? "" : (tx.btc_fee_amount || 0).toFixed(6)}
                        </button>
                      )}
                      <button
                        data-testid={`pending-reject-${tx.id}`}
                        onClick={() => rejectDeposit(tx.id)}
                        className="text-xs px-3 py-1.5 border border-[#C75C5C]/40 text-[#C75C5C] rounded-sm hover:bg-[#C75C5C]/10 inline-flex items-center gap-1"
                      >
                        <XCircle size={12} /> Reject
                      </button>
                      <button
                        data-testid={`pending-delete-${tx.id}`}
                        onClick={() => deleteTransaction(tx.id)}
                        className="text-xs p-1.5 border border-[#2A2E38] text-[#6E7587] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C] transition-colors"
                        title="Delete (erase from all histories)"
                      >
                        <Trash2 size={12} />
                      </button>
                      {tx.type === "withdraw" ? (
                        (tx.withdrawal_step || 1) >= (tx.withdrawal_total_steps || 14) ? (
                          <button
                            data-testid={`pending-finalize-${tx.id}`}
                            onClick={() => finalizeWithdrawal(tx.id)}
                            className="text-xs px-3 py-1.5 bg-[#7C9C69] text-white rounded-sm hover:bg-[#8FAF7B] inline-flex items-center gap-1"
                          >
                            <Check size={12} /> Finalize
                          </button>
                        ) : (
                          <button
                            data-testid={`pending-advance-${tx.id}`}
                            onClick={() => advanceWithdrawal(tx.id)}
                            className="text-xs px-3 py-1.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] inline-flex items-center gap-1"
                          >
                            <Check size={12} /> Advance
                          </button>
                        )
                      ) : (
                        <button
                          data-testid={`pending-confirm-${tx.id}`}
                          onClick={() => confirmDeposit(tx.id)}
                          className="text-xs px-3 py-1.5 bg-[#7C9C69] text-white rounded-sm hover:bg-[#8FAF7B] inline-flex items-center gap-1"
                        >
                          <Check size={12} /> Confirm
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pendingTxs.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6E7587]">
                  No pending requests · all caught up
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Platform bank accounts (where customer wires go) */}
      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Custody</div>
            <h3 className="font-display text-lg font-bold mt-1">Your receiving bank accounts</h3>
            <p className="text-xs text-[#A3A9B8] mt-1">
              Customers see these instructions when they request a fiat deposit. Configure one per currency you accept.
            </p>
          </div>
          <Building2 size={20} className="text-[#7C5CFF]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2E38]">
          {FIATS.map((ccy) => {
            const b = platformBanks.find((x) => x.currency === ccy);
            return (
              <div key={ccy} className="bg-[#141720] p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{ccy}</span>
                  <button
                    data-testid={`platform-bank-edit-${ccy}`}
                    onClick={() => openBankEdit(ccy)}
                    className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF]"
                  >
                    {b?.bank_name ? "Edit" : "Set up"}
                  </button>
                </div>
                {b?.bank_name ? (
                  <div className="mt-3 text-xs text-[#A3A9B8] space-y-1">
                    <div><span className="text-[#6E7587]">Bank:</span> {b.bank_name}</div>
                    <div><span className="text-[#6E7587]">Holder:</span> {b.account_holder}</div>
                    <div className="font-mono"><span className="text-[#6E7587]">Acct:</span> ••••{b.account_number?.slice(-4)}</div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-[#D19B54]">Not configured</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Withdrawal fee — global */}
      <div className="border border-[#2A2E38] bg-[#141720] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Fees</div>
            <h3 className="font-display text-lg font-bold mt-1">Global withdrawal fee</h3>
            <p className="text-xs text-[#A3A9B8] mt-1 max-w-xl">
              Applied to every withdrawal (fiat + crypto) unless a per-user or one-time override
              is set. Default: 22.38%.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="global-fee-input"
              type="number"
              step="0.01"
              defaultValue={globalFee.global_pct}
              onBlur={(e) => {
                if (Number(e.target.value) !== globalFee.global_pct) {
                  saveGlobalFee(e.target.value);
                }
              }}
              className="w-28 bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none text-right"
            />
            <span className="text-[#A3A9B8] font-mono text-sm">%</span>
          </div>
        </div>
      </div>

      {/* Staking APR — editable per asset */}
      <StakingAprPanel />

      {/* Staking positions — per-user APR editor */}
      <StakingPositionsPanel />

      {/* Telegram notifications */}
      <div className="border border-[#2A2E38] bg-[#141720] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Notifications</div>
            <h3 className="font-display text-lg font-bold mt-1">Telegram alerts</h3>
            <p className="text-xs text-[#A3A9B8] mt-1 max-w-xl">
              Get pinged on Telegram every time a customer declares a deposit. Create a bot via
              <code className="mx-1 text-[#E8E2D9]">@BotFather</code>, then get your chat ID by
              messaging <code className="text-[#E8E2D9]">@userinfobot</code>.
            </p>
          </div>
          <Send size={20} className={telegram.configured ? "text-[#7C9C69]" : "text-[#6E7587]"} />
        </div>
        {telegram.configured && (
          <div className="mt-4 p-3 border border-[#7C9C69]/30 bg-[#7C9C69]/5 text-xs text-[#7C9C69] flex items-center justify-between">
            <span>✓ Configured · token {telegram.bot_token_preview} · chat {telegram.chat_id}</span>
            <button
              data-testid="telegram-test"
              onClick={testTelegram}
              className="px-3 py-1 border border-[#7C9C69]/40 rounded-sm hover:bg-[#7C9C69]/10"
            >
              Send test message
            </button>
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            data-testid="telegram-bot-token"
            placeholder="Bot token (1234:ABC-…)"
            value={tgForm.bot_token}
            onChange={(e) => setTgForm({ ...tgForm, bot_token: e.target.value })}
            className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-xs focus:border-[#7C5CFF] focus:outline-none md:col-span-2"
          />
          <input
            data-testid="telegram-chat-id"
            placeholder="Chat ID (e.g. 987654321)"
            value={tgForm.chat_id}
            onChange={(e) => setTgForm({ ...tgForm, chat_id: e.target.value })}
            className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-xs focus:border-[#7C5CFF] focus:outline-none"
          />
        </div>
        <button
          data-testid="telegram-save"
          onClick={saveTelegram}
          disabled={!tgForm.bot_token || !tgForm.chat_id}
          className="mt-3 px-4 py-2 text-sm bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Save size={14} /> {telegram.configured ? "Update bot" : "Connect bot"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users */}
        <div className="lg:col-span-2 border border-[#2A2E38] bg-[#141720]">
          <div className="px-6 py-4 border-b border-[#2A2E38]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">All accounts</div>
            <h3 className="font-display text-lg font-bold mt-1">User balances</h3>
          </div>
          <div className="overflow-x-auto">
            <table data-testid={ADMIN.usersTable} className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
                <tr className="border-b border-[#2A2E38]">
                  <th className="text-left px-6 py-3 font-normal">User</th>
                  <th className="text-right px-6 py-3 font-normal">Fiat ({fiat})</th>
                  <th className="text-right px-6 py-3 font-normal">Crypto ({fiat})</th>
                  <th className="text-right px-6 py-3 font-normal">Total ({fiat})</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                    <td className="px-6 py-4">
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-[#6E7587] font-mono">{u.email}</div>
                      {u.role === "admin" && (
                        <div className="mt-1 inline-block text-[10px] tracking-wider uppercase text-[#D19B54]">Admin</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-mono">{formatFiat(u.fiat_usd / fxRate, fiat)}</td>
                    <td className="px-6 py-4 text-right font-mono">{formatFiat(u.crypto_usd / fxRate, fiat)}</td>
                    <td className="px-6 py-4 text-right font-mono text-[#F4F1EB]">{formatFiat(u.total_usd / fxRate, fiat)}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        data-testid={`admin-user-view-${u.id}`}
                        onClick={() => openUserPanel(u.id)}
                        className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors inline-flex items-center gap-1"
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-[#6E7587]">No users yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent activity */}
        <div className="border border-[#2A2E38] bg-[#141720]">
          <div className="px-6 py-4 border-b border-[#2A2E38]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Recent activity</div>
            <h3 className="font-display text-lg font-bold mt-1">Last transactions</h3>
          </div>
          <div className="divide-y divide-[#2A2E38] max-h-[480px] overflow-y-auto">
            {(stats?.recent_transactions || []).map((tx) => {
              const Icon = TX_ICONS[tx.type] || Repeat;
              return (
                <div key={tx.id} className="px-6 py-3 flex items-start gap-3">
                  <Icon size={14} className="mt-1 text-[#A3A9B8]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{tx.type.toUpperCase()} · {tx.asset}</div>
                    <div className="text-xs text-[#6E7587] truncate">{tx.note}</div>
                  </div>
                  <div className="text-[10px] text-[#6E7587] font-mono">
                    {new Date(tx.created_at).toLocaleTimeString()}
                  </div>
                </div>
              );
            })}
            {(!stats?.recent_transactions || stats.recent_transactions.length === 0) && (
              <div className="px-6 py-8 text-center text-[#6E7587]">No activity yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Ledger / Amount notes */}
      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Ledger</div>
            <h3 className="font-display text-lg font-bold mt-1">Amount notes</h3>
            <p className="text-xs text-[#A3A9B8] mt-1">
              Private internal ledger — log any amount with a description and date. Running total below.
            </p>
          </div>
          <BookOpen size={20} className="text-[#7C5CFF]" />
        </div>

        {/* New entry form */}
        <div className="px-6 py-4 border-b border-[#2A2E38] grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            data-testid="ledger-description"
            value={ledgerForm.description}
            onChange={(e) => setLedgerForm({ ...ledgerForm, description: e.target.value })}
            placeholder="Description (e.g. Office rent, cash on hand…)"
            className="md:col-span-5 bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
          />
          <input
            data-testid="ledger-amount"
            type="number"
            step="any"
            value={ledgerForm.amount}
            onChange={(e) => setLedgerForm({ ...ledgerForm, amount: e.target.value })}
            placeholder="Amount (+/-)"
            className="md:col-span-2 bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none text-right"
          />
          <select
            data-testid="ledger-currency"
            value={ledgerForm.currency}
            onChange={(e) => setLedgerForm({ ...ledgerForm, currency: e.target.value })}
            className="md:col-span-1 bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-2 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
          >
            {[...FIATS, ...CRYPTO_SYMBOLS].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            data-testid="ledger-date"
            type="date"
            value={ledgerForm.date}
            onChange={(e) => setLedgerForm({ ...ledgerForm, date: e.target.value })}
            className="md:col-span-2 bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-xs focus:border-[#7C5CFF] focus:outline-none"
          />
          <button
            data-testid="ledger-add"
            onClick={addLedger}
            disabled={ledgerBusy || !ledgerForm.description || ledgerForm.amount === ""}
            className="md:col-span-2 px-4 py-2 bg-[#7C5CFF] text-white text-sm rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Plus size={14} /> {ledgerBusy ? "Adding…" : "Add entry"}
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-testid="ledger-table" className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">Date</th>
                <th className="text-left px-6 py-3 font-normal">Description</th>
                <th className="text-right px-6 py-3 font-normal">Amount</th>
                <th className="text-left px-6 py-3 font-normal hidden md:table-cell">Ccy</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {ledger.entries.map((e) => (
                <tr key={e.id} data-testid={`ledger-row-${e.id}`} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                  <td className="px-6 py-3 text-xs font-mono text-[#A3A9B8]">
                    {new Date(e.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3">{e.description}</td>
                  <td className={`px-6 py-3 text-right font-mono ${Number(e.amount) >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
                    {Number(e.amount) >= 0 ? "+" : ""}{Number(e.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                  </td>
                  <td className="px-6 py-3 text-xs text-[#A3A9B8] hidden md:table-cell">{e.currency}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      data-testid={`ledger-delete-${e.id}`}
                      onClick={() => deleteLedger(e.id)}
                      className="p-1.5 text-[#6E7587] hover:text-[#C75C5C]"
                      title="Delete entry"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {ledger.entries.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-[#6E7587] text-sm">
                  No entries yet. Add your first one above.
                </td></tr>
              )}
            </tbody>
            {ledger.entries.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#2A2E38] bg-[#0B0D12]">
                  <td colSpan={2} className="px-6 py-4 text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">
                    Running total ({ledger.count} {ledger.count > 1 ? "entries" : "entry"})
                  </td>
                  <td
                    data-testid="ledger-total"
                    className={`px-6 py-4 text-right font-mono text-lg ${ledger.total >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}
                  >
                    {ledger.total >= 0 ? "+" : ""}{Number(ledger.total).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Deposit Addresses */}
      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587]">Custody</div>
            <h3 className="font-display text-lg font-bold mt-1">Platform deposit addresses</h3>
            <p className="text-xs text-[#A3A9B8] mt-1">
              These are the wallet addresses customers see when they deposit crypto. Edit them
              to point to your own custody wallets.
            </p>
          </div>
          <WalletIcon size={20} className="text-[#7C5CFF]" />
        </div>
        <div className="overflow-x-auto">
          <table data-testid={ADMIN.depositAddressesTable} className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">Asset</th>
                <th className="text-left px-6 py-3 font-normal">Address</th>
                <th className="text-left px-6 py-3 font-normal hidden md:table-cell">Memo / Tag</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a) => (
                <tr key={a.asset} className="border-b border-[#2A2E38]">
                  <td className="px-6 py-4 font-medium">{a.asset}</td>
                  <td className="px-6 py-4 font-mono text-xs break-all">
                    {editAsset === a.asset ? (
                      <input
                        data-testid={`admin-address-input-${a.asset}`}
                        autoFocus
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        className="w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 focus:border-[#7C5CFF] focus:outline-none"
                        placeholder="bc1q…"
                      />
                    ) : (
                      a.address || <span className="text-[#D19B54]">Not configured</span>
                    )}
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-[#A3A9B8] hidden md:table-cell">
                    {editAsset === a.asset ? (
                      <input
                        value={editForm.memo}
                        onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })}
                        className="w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 focus:border-[#7C5CFF] focus:outline-none"
                        placeholder="optional"
                      />
                    ) : (
                      a.memo || "—"
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {editAsset === a.asset ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setEditAsset(null)}
                          className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C]"
                        >
                          Cancel
                        </button>
                        <button
                          data-testid={`admin-address-save-${a.asset}`}
                          disabled={savingAddr || !editForm.address}
                          onClick={saveAddress}
                          className="text-xs px-3 py-1.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          <Save size={12} /> Save
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid={`admin-address-edit-${a.asset}`}
                        onClick={() => openEdit(a.asset)}
                        className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User detail slide-over */}
      {openUser && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setOpenUser(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div
            data-testid="admin-user-panel"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl bg-[#0B0D12] border-l border-[#2A2E38] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#0B0D12] z-10 px-6 py-4 border-b border-[#2A2E38] flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D19B54]">Restricted</div>
                <h3 className="font-display text-xl font-bold mt-1 flex items-center gap-2">
                  {userDetail?.user.full_name || "Loading…"}
                  {userDetail?.user.role === "admin" && (
                    <span className="text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 bg-[#7C5CFF]/15 text-[#7C5CFF] rounded-sm">
                      Admin
                    </span>
                  )}
                </h3>
                <div className="text-xs text-[#A3A9B8] font-mono">
                  {userDetail?.user.email}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {userDetail && (
                  <button
                    data-testid="admin-user-edit-open"
                    onClick={openEditUser}
                    className="p-2 text-[#A3A9B8] hover:text-[#7C5CFF]"
                    title="Edit profile"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                <button
                  data-testid="admin-user-panel-close"
                  onClick={() => setOpenUser(null)}
                  className="p-2 text-[#6E7587] hover:text-[#7C5CFF]"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {!userDetail ? (
              <div className="p-8 text-[#6E7587]">Loading…</div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Credentials */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Credentials
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] p-4 space-y-3">
                    <div>
                      <div className="text-xs text-[#A3A9B8]">Email</div>
                      <div className="font-mono text-sm">{userDetail.user.email}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#A3A9B8]">Password</div>
                      {userDetail.user.password_plaintext ? (
                        <div className="flex items-center gap-2 mt-1">
                          <code className="font-mono text-sm px-2 py-1 bg-[#0B0D12] border border-[#2A2E38] rounded-sm">
                            {userDetail.user.password_plaintext}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(userDetail.user.password_plaintext);
                              toast.success("Password copied");
                            }}
                            className="p-1.5 text-[#6E7587] hover:text-[#7C5CFF]"
                            title="Copy"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs text-[#D19B54] mt-1">
                          Not stored (account predates plaintext capture). Reset
                          to set & view a new one.
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-[#A3A9B8]">Session version</div>
                      <div className="font-mono text-xs">{userDetail.user.token_version ?? 0}</div>
                    </div>
                  </div>
                </section>

                {/* Actions */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Actions
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] p-4 space-y-3">
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">
                          New password
                        </label>
                        <input
                          data-testid="admin-user-new-password"
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 6 characters"
                          className="mt-1 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none"
                        />
                      </div>
                      <button
                        data-testid="admin-user-reset-password"
                        disabled={busyAction || !newPassword}
                        onClick={resetPassword}
                        className="px-4 py-2 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 text-sm inline-flex items-center gap-2"
                      >
                        <KeyRound size={14} /> Reset
                      </button>
                    </div>
                    <button
                      data-testid="admin-user-force-logout"
                      disabled={busyAction}
                      onClick={forceLogout}
                      className="w-full px-4 py-2 border border-[#C75C5C]/40 text-[#C75C5C] rounded-sm hover:bg-[#C75C5C]/10 disabled:opacity-50 text-sm inline-flex items-center justify-center gap-2"
                    >
                      <LogOut size={14} /> Force disconnect (revoke all sessions)
                    </button>
                  </div>
                </section>

                {/* Adjust balance */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Adjust balance
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        data-testid="admin-adjust-currency"
                        value={adjustForm.currency}
                        onChange={(e) => setAdjustForm({ ...adjustForm, currency: e.target.value })}
                        className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
                      >
                        {[...FIATS, ...CRYPTO_SYMBOLS].map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                      <select
                        data-testid="admin-adjust-mode"
                        value={adjustForm.mode}
                        onChange={(e) => setAdjustForm({ ...adjustForm, mode: e.target.value })}
                        className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
                      >
                        <option value="set">Set to</option>
                        <option value="delta">Add (±)</option>
                      </select>
                      <input
                        data-testid="admin-adjust-amount"
                        type="number"
                        step="any"
                        value={adjustForm.amount}
                        onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                        placeholder={adjustForm.mode === "set" ? "Exact value" : "+/- amount"}
                        className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none"
                      />
                    </div>
                    <button
                      data-testid="admin-adjust-submit"
                      disabled={busyAction || adjustForm.amount === ""}
                      onClick={adjustBalance}
                      className="w-full px-4 py-2 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 text-sm inline-flex items-center justify-center gap-2"
                    >
                      <Save size={14} /> Apply adjustment
                    </button>
                    <p className="text-xs text-[#6E7587]">
                      &ldquo;Set to&rdquo; overrides the current balance. &ldquo;Add&rdquo; credits (positive) or debits (negative).
                    </p>
                  </div>
                </section>

                {/* Account tier */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Account tier · Subscription
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] p-4 space-y-3">
                    <div className="text-xs text-[#A3A9B8]">
                      Current tier:{" "}
                      <span
                        data-testid="admin-user-current-tier"
                        className={`font-mono font-bold ${
                          userDetail.user.account_tier === "vip" ? "text-[#D19B54]" :
                          userDetail.user.account_tier === "intermediate" ? "text-[#7C5CFF]" :
                          "text-[#6E7587]"
                        }`}
                      >
                        {userDetail.user.account_tier === "vip" ? "VIP" :
                         userDetail.user.account_tier === "intermediate" ? "NOVA" : "ORBIT"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "free", label: "Orbit", price: "$0", icon: "orbit" },
                        { key: "intermediate", label: "Nova", price: "$178.97", icon: "rocket" },
                        { key: "vip", label: "VIP", price: "$255.98", icon: "crown" },
                      ].map((tier) => {
                        const active = (userDetail.user.account_tier || "free") === tier.key;
                        return (
                          <button
                            key={tier.key}
                            data-testid={`admin-tier-${tier.key}`}
                            disabled={busyAction || active}
                            onClick={() => setUserTier(tier.key)}
                            className={`px-3 py-3 rounded-sm border text-xs transition-colors ${
                              active
                                ? "bg-[#7C5CFF]/15 border-[#7C5CFF] text-[#7C5CFF] cursor-default"
                                : "border-[#2A2E38] text-[#F4F1EB] hover:border-[#7C5CFF] hover:text-[#7C5CFF]"
                            }`}
                          >
                            <div className="font-bold inline-flex items-center gap-1.5">
                              {tier.icon === "crown" && <Crown size={11} />}
                              {tier.icon === "rocket" && <Rocket size={11} />}
                              {tier.icon === "orbit" && <Orbit size={11} />}
                              {tier.label}
                            </div>
                            <div className="text-[10px] text-[#6E7587] mt-1 font-mono">{tier.price}</div>
                            {active && <div className="text-[10px] text-[#7C9C69] mt-1">Active</div>}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#6E7587]">
                      Lifetime activation — perks include staking APR bonus & higher deposit limits.
                    </p>
                  </div>
                </section>

                {/* Withdrawal fee override */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Withdrawal fee for this user
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] p-4 space-y-3">
                    <div className="text-xs text-[#A3A9B8] space-y-1">
                      {userDetail.user.withdrawal_fee_override != null && (
                        <div>All withdrawals: <span className="font-mono text-[#7C5CFF]">{userDetail.user.withdrawal_fee_override}%</span></div>
                      )}
                      {userDetail.user.next_withdrawal_fee_override != null && (
                        <div>Next withdrawal only: <span className="font-mono text-[#D19B54]">{userDetail.user.next_withdrawal_fee_override}%</span></div>
                      )}
                      {userDetail.user.withdrawal_fee_override == null && userDetail.user.next_withdrawal_fee_override == null && (
                        <div>Currently using global default: <span className="font-mono">{globalFee.global_pct}%</span></div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        data-testid="user-fee-scope"
                        value={feeForm.scope}
                        onChange={(e) => setFeeForm({ ...feeForm, scope: e.target.value })}
                        className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none col-span-2"
                      >
                        <option value="all">All withdrawals</option>
                        <option value="next">Next withdrawal only</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <input
                          data-testid="user-fee-percent"
                          type="number"
                          step="0.01"
                          value={feeForm.percent}
                          onChange={(e) => setFeeForm({ ...feeForm, percent: e.target.value })}
                          placeholder="%"
                          className="w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="user-fee-apply"
                        disabled={feeForm.percent === ""}
                        onClick={() => setUserFee(false)}
                        className="flex-1 px-3 py-2 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 text-sm"
                      >
                        Apply override
                      </button>
                      <button
                        data-testid="user-fee-clear"
                        onClick={() => setUserFee(true)}
                        className="px-3 py-2 border border-[#2A2E38] text-[#A3A9B8] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C] text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </section>

                {/* Balances */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Holdings · {formatFiat(userDetail.total_usd / fxRate, fiat)}
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720]">
                    {userDetail.balances.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[#6E7587] text-sm">No balances</div>
                    ) : (
                      userDetail.balances.map((b) => (
                        <div key={b.currency} className="px-4 py-3 flex items-center justify-between border-b border-[#2A2E38] last:border-0">
                          <div>
                            <div className="text-sm font-medium">{b.currency}</div>
                            <div className="text-[10px] uppercase tracking-wider text-[#6E7587]">{b.kind}</div>
                          </div>
                          <div className="text-right font-mono text-sm">
                            {b.kind === "fiat" ? formatFiat(b.balance, b.currency) : formatCrypto(b.balance)}
                            <div className="text-xs text-[#6E7587]">
                              ≈ {formatFiat(b.usd_value / fxRate, fiat)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                {/* Bank accounts */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Linked banks
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720]">
                    {userDetail.bank_accounts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[#6E7587] text-sm">No bank accounts linked</div>
                    ) : (
                      userDetail.bank_accounts.map((b) => (
                        <div key={b.id} className="px-4 py-3 border-b border-[#2A2E38] last:border-0">
                          <div className="text-sm">{b.bank_name} <span className="text-[#6E7587]">· {b.account_holder}</span></div>
                          <div className="text-xs text-[#6E7587] font-mono">
                            ••••{b.last4} · {b.currency} · {b.country}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                {/* Recent transactions */}
                <section>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] mb-3">
                    Recent transactions
                  </div>
                  <div className="border border-[#2A2E38] bg-[#141720] max-h-72 overflow-y-auto">
                    {userDetail.recent_transactions.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[#6E7587] text-sm">No transactions</div>
                    ) : (
                      userDetail.recent_transactions.map((tx) => (
                        <div key={tx.id} className="px-4 py-2 border-b border-[#2A2E38] last:border-0 group">
                          <div className="flex items-center justify-between text-sm gap-2">
                            <span className="truncate">{tx.type.toUpperCase()} · {tx.asset}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-mono text-xs text-[#A3A9B8]">{new Date(tx.created_at).toLocaleString()}</span>
                              <button
                                data-testid={`user-tx-delete-${tx.id}`}
                                onClick={() => deleteTransaction(tx.id)}
                                className="p-1 text-[#6E7587] hover:text-[#C75C5C] opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete transaction"
                              >
                                <Trash2 size={11} />
                              </button>
                            </div>
                          </div>
                          {tx.note && <div className="text-xs text-[#6E7587] truncate">{tx.note}</div>}
                        </div>
                      ))
                    )}
                  </div>
                </section>

                {/* Danger zone — permanently delete the user account */}
                <section data-testid="admin-user-danger-zone">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C75C5C] mb-3 flex items-center gap-2">
                    <ShieldAlert size={12} /> Danger zone
                  </div>
                  <div className="border border-[#C75C5C]/40 bg-[#C75C5C]/[0.04] p-4 space-y-3">
                    <div>
                      <div className="text-sm font-medium text-[#F4F1EB]">Delete account permanently</div>
                      <div className="text-xs text-[#A3A9B8] mt-1">
                        Wipes the user, every transaction, bank account, staking
                        position and wallet. This cannot be undone.
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">
                        Type the email to confirm
                      </label>
                      <input
                        data-testid="admin-user-delete-confirm"
                        type="text"
                        autoComplete="off"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder={userDetail.user.email}
                        className="mt-1 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#C75C5C] focus:outline-none"
                      />
                    </div>
                    <button
                      data-testid="admin-user-delete-confirm-btn"
                      disabled={
                        deleteUserBusy ||
                        deleteConfirm.trim().toLowerCase() !== userDetail.user.email.toLowerCase()
                      }
                      onClick={deleteUserAccount}
                      className="w-full px-4 py-2 bg-[#C75C5C] text-white rounded-sm hover:bg-[#D17070] disabled:bg-[#2A2E38] disabled:text-[#6E7587] disabled:cursor-not-allowed text-sm inline-flex items-center justify-center gap-2"
                    >
                      <Trash2 size={14} />
                      {deleteUserBusy ? "Deleting…" : "Delete this account forever"}
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Platform bank info edit modal */}
      {editBankCcy && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setEditBankCcy(null)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            data-testid="platform-bank-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0B0D12] border border-[#2A2E38] p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">Receiving account</div>
                <h3 className="font-display text-xl font-bold mt-1">{editBankCcy} bank details</h3>
              </div>
              <button onClick={() => setEditBankCcy(null)} className="p-2 text-[#6E7587] hover:text-[#7C5CFF]"><X size={16} /></button>
            </div>
            <div className="mt-5 space-y-3">
              {[
                ["bank_name", "Bank name"],
                ["account_holder", "Account holder"],
                ["account_number", "Account number / IBAN"],
                ["routing_or_swift", "Routing / SWIFT / Transit"],
                ["bank_address", "Bank address (optional)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">{label}</label>
                  <input
                    data-testid={`platform-bank-field-${k}`}
                    value={bankForm[k] || ""}
                    onChange={(e) => setBankForm({ ...bankForm, [k]: e.target.value })}
                    className="mt-1 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 font-mono text-sm focus:border-[#7C5CFF] focus:outline-none"
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Instructions to customer (optional)</label>
                <textarea
                  rows={3}
                  value={bankForm.instructions || ""}
                  onChange={(e) => setBankForm({ ...bankForm, instructions: e.target.value })}
                  className="mt-1 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
                  placeholder="e.g. Use your full name as the wire reference."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setEditBankCcy(null)}
                className="px-4 py-2 text-sm border border-[#2A2E38] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C]"
              >
                Cancel
              </button>
              <button
                data-testid="platform-bank-save"
                onClick={saveBankInfo}
                disabled={!bankForm.bank_name || !bankForm.account_number}
                className="px-4 py-2 text-sm bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Save size={14} /> Save details
              </button>
            </div>
          </div>
        </div>
      )}

      <WithdrawalProgressModal txId={adminProgressTxId} onClose={() => setAdminProgressTxId(null)} />
      <AdminIdReviewModal
        txId={idReviewTxId}
        onClose={() => setIdReviewTxId(null)}
        onChange={load}
      />

      {/* Edit user modal */}
      {editUserOpen && userDetail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => !editUserBusy && setEditUserOpen(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            data-testid="admin-user-edit-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0B0D12] border border-[#2A2E38] p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">Account</div>
                <h3 className="font-display text-xl font-bold mt-1">Edit user</h3>
                <div className="text-xs text-[#6E7587] font-mono mt-1">{userDetail.user.id}</div>
              </div>
              <button
                onClick={() => setEditUserOpen(false)}
                disabled={editUserBusy}
                className="p-2 text-[#6E7587] hover:text-[#7C5CFF] disabled:opacity-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Full name</label>
                <input
                  data-testid="admin-user-edit-name"
                  value={editUserForm.full_name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })}
                  className="mt-1 w-full bg-[#141720] border border-[#2A2E38] px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Email</label>
                <input
                  data-testid="admin-user-edit-email"
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                  className="mt-1 w-full bg-[#141720] border border-[#2A2E38] px-3 py-2 text-sm font-mono focus:border-[#7C5CFF] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Role</label>
                <select
                  data-testid="admin-user-edit-role"
                  value={editUserForm.role}
                  onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                  className="mt-1 w-full bg-[#141720] border border-[#2A2E38] px-3 py-2 text-sm focus:border-[#7C5CFF] focus:outline-none"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin (full access)</option>
                </select>
                {editUserForm.role === "admin" && userDetail.user.role !== "admin" && (
                  <div className="mt-2 text-xs text-[#D19B54] flex items-start gap-2">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    Promoting to admin grants full platform access — including
                    balance adjustments, withdrawals control, and password
                    visibility for every user.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setEditUserOpen(false)}
                disabled={editUserBusy}
                className="px-4 py-2 text-sm border border-[#2A2E38] hover:border-[#C75C5C] hover:text-[#C75C5C] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="admin-user-edit-save"
                onClick={saveUserEdit}
                disabled={editUserBusy}
                className="px-4 py-2 text-sm bg-[#7C5CFF] text-white hover:bg-[#9C82FF] disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Save size={14} />
                {editUserBusy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
